import * as React from "react";
import { AppBundleApplication, Application, ApplicationsService } from "gen/clients";
import { useQuery } from "react-query";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { AddApplicationDialog } from "./AddApplicationDialog";
import { useDialogState } from "hooks/useDialogState";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableColumn, DataTableRowAction } from "components/DataTable/types";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { ApplicationId } from "types/ApplicationId";
import { EditApplicationPresetDialog } from "./EditApplicationPresetDialog";

interface Props {
  applications: AppBundleApplication[];
  onChange: (applications: AppBundleApplication[]) => void;
  disabled?: boolean;
}

const ApplicationSelector: React.FC<Props> = ({ applications, onChange, disabled }) => {
  const [addDialogOpen, openAddDialog, closeAddDialog] = useDialogState();
  const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);
  const [editPresetIndex, setEditPresetIndex] = React.useState<number | null>(null);

  const { data: availableApplications, isLoading: isLoadingApplications } = useQuery(["listApplications"], {
    queryFn: ApplicationsService.listApplications
  });

  // Get unique application IDs that need full application details for preset names
  const applicationIdsWithPresets = React.useMemo(() => {
    return [...new Set(applications.filter(app => app.applicationSettingsPresetId).map(app => app.applicationId))];
  }, [applications]);

  // Fetch full application details for applications that have presets
  const applicationDetailsQueries = useQuery(
    ["applicationDetails", applicationIdsWithPresets],
    async () => {
      if (applicationIdsWithPresets.length === 0) {
        return {};
      }

      const details: Record<string, Application> = {};
      await Promise.all(
        applicationIdsWithPresets.map(async appId => {
          try {
            const app = await ApplicationsService.getApplication({ id: appId });
            details[appId] = app;
          } catch (error) {
            console.warn(`Failed to fetch application details for ${appId}:`, error);
          }
        })
      );
      return details;
    },
    {
      enabled: applicationIdsWithPresets.length > 0,
      staleTime: 5 * 60 * 1000 // Cache for 5 minutes
    }
  );

  const handleAddApplication = React.useCallback(
    (applicationId: string, presetId?: string) => {
      const newApplication: AppBundleApplication = {
        applicationId,
        applicationSettingsPresetId: presetId
      };

      onChange([...applications, newApplication]);
      closeAddDialog();
    },
    [applications, onChange, closeAddDialog]
  );

  const handleRemoveApplication = React.useCallback(
    (index: number) => {
      const updatedApplications = applications.filter((_, i) => i !== index);
      onChange(updatedApplications);
    },
    [applications, onChange]
  );

  const getApplicationName = React.useCallback(
    (applicationId: string) => {
      const app = availableApplications?.items?.find(a => a.id === applicationId);
      return app?.name || applicationId;
    },
    [availableApplications]
  );

  const getPresetName = React.useCallback(
    (applicationId: string, presetId?: string) => {
      if (!presetId) {
        return null;
      }

      const applicationDetails = applicationDetailsQueries.data?.[applicationId];
      if (!applicationDetails?.settingPresets) {
        return presetId; // Fallback to ID if details not available
      }

      const preset = applicationDetails.settingPresets.find(p => p.id === presetId);
      return preset?.name || presetId; // Fallback to ID if preset not found
    },
    [applicationDetailsQueries.data]
  );

  const availableApplicationIds = React.useMemo(() => {
    const usedIds = new Set(applications.map(app => app.applicationId));
    return availableApplications?.items?.filter(app => !usedIds.has(app.id)) || [];
  }, [applications, availableApplications]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Applications
      </Typography>

      <Box sx={{ mb: 2 }}>
        <DataTable
          columns={
            [
              {
                id: "name",
                title: <strong>Application Name</strong>,
                cell: (row: AppBundleApplication) => getApplicationName(row.applicationId)
              },
              {
                id: "preset",
                title: <strong>Configuration Preset</strong>,
                cell: (row: AppBundleApplication) =>
                  row.applicationId === ApplicationId.machineTranslation
                    ? getPresetName(row.applicationId, row.applicationSettingsPresetId) || "—"
                    : "—"
              }
            ] as DataTableColumn[]
          }
          data={applications}
          rowKey={(row: any) => `${row.applicationId}-${applications.indexOf(row)}`}
          rowActions={(row: AppBundleApplication, index: number) => {
            const actions: DataTableRowAction[] = [];
            const canEditPreset = row.applicationId === ApplicationId.machineTranslation;
            if (canEditPreset) {
              actions.push({
                title: "Edit Preset",
                action: () => setEditPresetIndex(index),
                disabled: !!disabled
              });
            }
            if (canEditPreset) {
              actions.push({ type: "DIVIDER", title: "divider" } as any);
            }
            actions.push({
              title: "Remove",
              action: () => setDeleteIndex(index),
              color: "error.main",
              disabled: !!disabled
            });
            return actions;
          }}
          isLoading={isLoadingApplications || applicationDetailsQueries.isLoading}
          dense={true}
          elevation={0}
          noData={
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No applications selected. Click "Add Application" to get started.
              </Typography>
            </Box>
          }
        />
      </Box>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={openAddDialog}
        disabled={disabled || isLoadingApplications || availableApplicationIds.length === 0}
        fullWidth
      >
        Add Application
      </Button>

      {availableApplicationIds.length === 0 && applications.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
          All available applications have been added
        </Typography>
      )}

      {addDialogOpen && (
        <AddApplicationDialog
          availableApplications={availableApplicationIds}
          onClose={closeAddDialog}
          onAdd={handleAddApplication}
        />
      )}

      {deleteIndex !== null && (
        <ConfirmationDialog
          title="Remove Application"
          content={<div>Are you sure you want to remove this application from the app bundle?</div>}
          onClose={() => setDeleteIndex(null)}
          onConfirm={() => {
            handleRemoveApplication(deleteIndex);
            setDeleteIndex(null);
          }}
          confirmationButtonColor="error"
        />
      )}

      {editPresetIndex !== null && applications[editPresetIndex] && (
        <EditApplicationPresetDialog
          applicationId={applications[editPresetIndex].applicationId}
          currentPresetId={applications[editPresetIndex].applicationSettingsPresetId}
          onClose={() => setEditPresetIndex(null)}
          onSave={(presetId: string) => {
            const updated = applications.map((a, i) =>
              i === editPresetIndex ? { ...a, applicationSettingsPresetId: presetId } : a
            );
            onChange(updated);
            setEditPresetIndex(null);
          }}
        />
      )}
    </Box>
  );
};

export { ApplicationSelector };
